import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, ArrowLeft, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authAPI } from '../services/api';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { toast } from 'sonner';

const MIN = 8;

export default function ResetPasswordPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const token = params.get('token') || '';

	const [pwd, setPwd] = useState('');
	const [confirm, setConfirm] = useState('');
	const [showPwd, setShowPwd] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	useDocumentMeta({
		title: `${t('auth.reset_title')} | Equi-Market`,
		description: t('auth.reset_subtitle'),
	});

	const tooShort = pwd.length > 0 && pwd.length < MIN;
	const mismatch = confirm.length > 0 && pwd !== confirm;
	const canSubmit = pwd.length >= MIN && pwd === confirm && !!token;

	const strength = useMemo(() => {
		let s = 0;
		if (pwd.length >= MIN) s++;
		if (/[A-Z]/.test(pwd)) s++;
		if (/[0-9]/.test(pwd)) s++;
		if (/[^A-Za-z0-9]/.test(pwd)) s++;
		return s; // 0..4
	}, [pwd]);
	const strengthBars = ['#E6E2D6', '#9B2C2C', '#D69E2E', '#5C7CFA', '#2F855A'];

	const submit = async (e) => {
		e.preventDefault();
		if (!canSubmit) return;
		setSubmitting(true);
		try {
			await authAPI.resetPassword(token, pwd);
			toast.success(t('auth.reset_success'));
			navigate('/auth');
		} catch (err) {
			toast.error(err?.response?.data?.detail || t('common.error'));
		} finally {
			setSubmitting(false);
		}
	};

	if (!token) {
		return (
			<div
				className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2] flex items-start md:items-center justify-center"
				data-testid="reset-password-page"
			>
				<div className="w-full max-w-md mx-auto px-4 sm:px-6 py-10">
					<div className="bg-white border border-[#E6E2D6] rounded-sm p-6 sm:p-8 text-center">
						<div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FBE7E7] mb-4">
							<AlertCircle className="w-6 h-6 text-[#9B2C2C]" />
						</div>
						<h1 className="text-xl font-medium text-[#1A3C34] font-['Playfair_Display'] mb-2">
							{t('auth.reset_missing_token_title')}
						</h1>
						<p className="text-sm text-[#666055] mb-5">{t('auth.reset_missing_token_body')}</p>
						<Link to="/forgot-password" data-testid="reset-request-new-link">
							<Button className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm">
								{t('auth.forgot_submit')}
							</Button>
						</Link>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2] flex items-start md:items-center justify-center"
			data-testid="reset-password-page"
		>
			<div className="w-full max-w-md mx-auto px-4 sm:px-6 py-10">
				<div className="bg-white border border-[#E6E2D6] rounded-sm p-6 sm:p-8">
					<Link
						to="/auth"
						className="inline-flex items-center gap-1 text-xs text-[#8B4513] hover:underline mb-5"
						data-testid="reset-back-link"
					>
						<ArrowLeft className="w-3 h-3" /> {t('auth.back_to_login')}
					</Link>

					<div className="flex items-center gap-2 mb-1">
						<Lock className="w-5 h-5 text-[#8B4513]" />
						<h1 className="text-2xl font-medium text-[#1A3C34] font-['Playfair_Display']">
							{t('auth.reset_title')}
						</h1>
					</div>
					<p className="text-sm text-[#666055] mb-6">{t('auth.reset_subtitle')}</p>

					<form onSubmit={submit} className="space-y-4" data-testid="reset-form">
						<div>
							<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
								{t('auth.reset_new_password')}
							</Label>
							<div className="relative mt-1">
								<Input
									type={showPwd ? 'text' : 'password'}
									autoComplete="new-password"
									value={pwd}
									onChange={(e) => setPwd(e.target.value)}
									className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-12 pr-10"
									data-testid="reset-new-password-input"
									required
								/>
								<button
									type="button"
									onClick={() => setShowPwd((v) => !v)}
									className="absolute inset-y-0 right-3 flex items-center text-[#666055] hover:text-[#1A3C34]"
									aria-label={showPwd ? 'Hide password' : 'Show password'}
									data-testid="reset-toggle-visibility-btn"
								>
									{showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
								</button>
							</div>
							{/* Strength meter */}
							<div className="flex gap-1 mt-2">
								{[1, 2, 3, 4].map((i) => (
									<div
										key={i}
										className="h-1 flex-1 rounded-sm transition-colors"
										style={{ backgroundColor: i <= strength ? strengthBars[strength] : strengthBars[0] }}
									/>
								))}
							</div>
							<p className={`text-[11px] mt-1 ${tooShort ? 'text-[#9B2C2C]' : 'text-[#666055]/70'}`}>
								{t('auth.reset_min_chars', { n: MIN })}
							</p>
						</div>

						<div>
							<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
								{t('auth.reset_confirm_password')}
							</Label>
							<Input
								type={showPwd ? 'text' : 'password'}
								autoComplete="new-password"
								value={confirm}
								onChange={(e) => setConfirm(e.target.value)}
								className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-12 mt-1"
								data-testid="reset-confirm-password-input"
								required
							/>
							{mismatch && (
								<p className="text-[11px] mt-1 text-[#9B2C2C]" data-testid="reset-mismatch-error">
									{t('auth.reset_mismatch')}
								</p>
							)}
						</div>

						<Button
							type="submit"
							disabled={!canSubmit || submitting}
							className="w-full bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-12 text-base font-medium btn-equi"
							data-testid="reset-submit-btn"
						>
							{submitting ? t('common.loading') : t('auth.reset_submit')}
						</Button>
					</form>
				</div>
			</div>
		</div>
	);
}